/**
 * The rim: everyone the hero arrow is *not* pointing at.
 *
 * A room of six cannot have six arrows — that is a compass rose with a crowd on it, and it
 * answers no question at all. So one person gets the arrow and the rest get a chevron on the
 * edge of the screen at their bearing, with an initial and a distance. Glance down and you know
 * the shape of the group; tap one and the arrow swings to them.
 *
 * The chevrons only appear when a heading does. Without a compass their positions would be
 * meaningless, and a ring of confidently-placed markers that all point north is precisely the
 * confident lie the product refuses to tell.
 */

import { formatDistance, type ParticipantId } from '@beeline/shared';

import { el } from '../lib/dom';
import type { MemberGeometry } from '../lib/geometry';

export interface RimOptions {
  /** Tapping a chevron promotes that member to the hero arrow. */
  onFocus: (id: ParticipantId) => void;
}

export interface Rim {
  element: HTMLElement;
  update: (others: MemberGeometry[], headingAvailable: boolean) => void;
  destroy: () => void;
}

/** The letter in the chevron. A first initial where there is a name, the slot letter otherwise. */
const initialOf = (name: string | null, id: ParticipantId): string =>
  (name?.trim()[0] ?? id).toUpperCase();

export function createRim(options: RimOptions): Rim {
  const element = el('div', { class: 'rim', 'aria-hidden': 'false' });
  /** Chevrons are reused across frames so that a phone is not rebuilding eight nodes a second. */
  const nodes = new Map<
    ParticipantId,
    { root: HTMLElement; letter: HTMLElement; distance: HTMLElement }
  >();

  const nodeFor = (id: ParticipantId): NonNullable<ReturnType<typeof nodes.get>> => {
    const existing = nodes.get(id);
    if (existing) return existing;

    const letter = el('span', { class: 'rim__initial' });
    const distance = el('span', { class: 'rim__distance mono' });
    const root = el(
      'button',
      {
        class: 'rim__chevron',
        type: 'button',
        onclick: () => options.onFocus(id),
      },
      letter,
      distance,
    );

    const created = { root, letter, distance };
    nodes.set(id, created);
    element.append(root);
    return created;
  };

  return {
    element,

    update: (others, headingAvailable) => {
      element.hidden = !headingAvailable || others.length === 0;
      if (element.hidden) return;

      const seen = new Set<ParticipantId>();

      for (const { member, geometry } of others) {
        // Somebody we cannot place gets no chevron. A marker at a guessed angle is worse than
        // no marker: it says "they are over there" when we do not know that.
        if (geometry.relative === null) continue;
        seen.add(member.id);

        const node = nodeFor(member.id);
        node.letter.textContent = initialOf(member.name, member.id);
        node.distance.textContent =
          geometry.distanceMetres === null ? '' : formatDistance(geometry.distanceMetres);

        // Rotate out to the rim, then counter-rotate the label so the text stays upright.
        node.root.style.setProperty('--rim-angle', `${geometry.relative}deg`);
        node.root.classList.toggle('rim__chevron--stale', member.staleness !== 'live');
        node.root.classList.toggle('rim__chevron--arrived', member.converged);
        node.root.setAttribute(
          'aria-label',
          `${member.name ?? `Person ${member.id.toUpperCase()}`}, ${
            geometry.distanceMetres === null
              ? 'distance unknown'
              : formatDistance(geometry.distanceMetres)
          }. Point the arrow at them.`,
        );
        node.root.hidden = false;
      }

      for (const [id, node] of nodes) {
        if (!seen.has(id)) node.root.hidden = true;
      }
    },

    destroy: () => {
      nodes.clear();
      element.remove();
    },
  };
}
